// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import buttonSizeVariants from "@vanmeter/MobiusOverrides/Button/buttonSizeVariants";
import { fontConfig } from "@vanmeter/XDL/constants";
import PaginationArrowLeft from "@vanmeter/XDL/Icons/IconPaginationArrowLeft";
import PaginationArrowRight from "@vanmeter/XDL/Icons/IconPaginationArrowRight";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            cssOverrides: {
                formField: css`
                    display: flex;
                `,
                formInputWrapper: css`
                    width: auto;
                    max-width: 75px;
                    min-width: 41px;
                `,
                inputSelect: css`
                    font-size: ${fontConfig.small.size}px;
                `,
            },
            iconProps: {
                css: css`
                    &&& {
                        background-color: transparent;
                        padding-right: 4px;
                        width: auto;
                    }
                `,
            },
            labelProps: {
                mergeCss: true,
                transform: "capitalize",
                weight: fontConfig.weight.regular,
                color: "text",
                css: css`
                    min-height: initial;
                    letter-spacing: 0;
                    font-size: ${fontConfig.small.size}px;
                    line-height: ${fontConfig.small.lineHeight};
                    display: flex;
                    align-items: center;
                    width: fit-content;
                    ${xScreen("lte", "sm")} {
                        white-space: nowrap;
                    }
                `,
            },
            css: css`
                &&&&&&,
                &&&&&&:focus {
                    padding-right: 17px;
                }
                &&[aria-expanded="true"] ~ ${IconWrapper} {
                    padding-left: 4px;
                }
            `,
        },
        inputProps: {
            cssOverrides: {
                formField: css`
                    display: flex;
                `,
                formInputWrapper: css`
                    width: auto;
                    max-width: 65px;
                    min-width: 41px;
                `,
            },
        },
        navIconsSrc: {
            firstPage: "ChevronsLeft",
            previousPage: PaginationArrowLeft,
            nextPage: PaginationArrowRight,
            lastPage: "ChevronsRight",
        },

        buttonProps: {
            mergeCss: true,
            sizeVariant: "small",
            typographyProps: {
                ...fontConfig.regularSingleLine,
                css: css`
                    letter-spacing: 0;
                `,
            },
            css: css`
                min-height: ${buttonSizeVariants["small"].height}px;
                border: none;
                margin: 0;
                ${IconWrapper} {
                    color: ${color("common.ui6")};
                }

                &:hover:not(:disabled) {
                    background: ${color("common.ui1")};
                    color: ${color("common.ui1")};
                    ${IconWrapper} {
                        color: ${color("common.ui15")};
                    }
                }
                &:focus {
                    background: transparent;
                    ${IconWrapper} {
                        color: ${color("common.ui16")};
                    }
                }
                ${({ isPaginationNumber }: any) =>
                    isPaginationNumber &&
                    css`
                        border: 1px solid ${color("common.ui5")};
                        border-radius: 2px;
                        & ${TypographyStyle} {
                            font-weight: ${fontConfig.weight.regular};
                            font-family: ${fontConfig.fontFamily};
                        }
                        &:hover:not(:disabled) {
                            background: ${color("common.ui15")};
                        }
                        &:focus,
                        &[aria-current] {
                            background: ${color("common.ui16")};
                            border-color: ${color("common.ui16")};
                        }
                    `}
            `,
        },
        cssOverrides: {
            ...PaginationPresentationPropsDefault?.cssOverrides,
            ellipsis: css`
                min-height: ${buttonSizeVariants["small"].height}px;
                border: 1px solid ${color("common.ui5")};
                background-color: ${color("common.white")};
                border-radius: 2px;
                padding: 0 8px;
                vertical-align: bottom;
            `,
            linkList: css`
                & > ul {
                    display: flex;
                    align-items: center;
                }
                & > .pagination-container {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    & > .pagination-element {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        & .page-count {
                            white-space: nowrap;
                            text-transform: lowercase;
                        }
                    }
                }

                li {
                    margin: 0 4px;
                }
            `,
            pagination: css`
                gap: 38px;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 20px 0;
                                flex-wrap: nowrap;
                            `,
                            css`
                                flex-wrap: nowrap;
                            `,
                            null,
                            css`
                                margin: 20px 0;
                            `,
                            null,
                        ],
                        "min",
                    )};
                margin: 12px 0 16px 0;
                ${({ justifyContent }: any) => justifyContent && `justify-content: ${justifyContent};`}
                ${xScreen("lte", "sm")} {
                    justify-content: space-between;
                    width: 100%;
                }
            `,
        },
    },
} as xBaseTheme["pagination"];
